/**
 * User Form Modal - Create or edit user modal dialog.
 * Handles both creation and updating of users with form validation.
 */

import { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Alert,
  OutlinedInput,
  Chip,
} from '@mui/material';
import type { UserListItem, UserCreateRequest, UserUpdateRequest } from '@/types/admin';
import type { Role } from '@/types/auth';

interface UserFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (userData: UserCreateRequest | UserUpdateRequest) => Promise<void>;
  user: UserListItem | null; // null = create mode, non-null = edit mode
  roles: Role[];
}

export const UserFormModal: FC<UserFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  user,
  roles,
}) => {
  const isEditMode = user !== null;

  // Form state
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [mustChangePassword, setMustChangePassword] = useState(true);
  const [isActive, setIsActive] = useState(true);

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Initialize form with user data when editing
  useEffect(() => {
    if (user) {
      setUsername(user.username);
      setEmail(user.email);
      setFullName(user.full_name);
      setSelectedRoleIds(user.roles.map((r) => r.id));
      setIsActive(user.is_active);
      setMustChangePassword(user.must_change_password);
    } else {
      // Reset form for create mode
      setUsername('');
      setEmail('');
      setFullName('');
      setPassword('');
      setConfirmPassword('');
      setSelectedRoleIds([]);
      setMustChangePassword(true);
      setIsActive(true);
    }
    setErrors({});
  }, [user, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Username validation (only for create mode)
    if (!isEditMode) {
      if (!username.trim()) {
        newErrors.username = 'Username is required';
      } else if (username.length < 3) {
        newErrors.username = 'Username must be at least 3 characters';
      } else if (!/^[a-zA-Z0-9_]+$/.test(username)) {
        newErrors.username = 'Username can only contain letters, numbers, and underscores';
      }
    }

    // Email validation
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    // Full name validation
    if (!fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    // Password validation (only for create mode)
    if (!isEditMode) {
      if (!password) {
        newErrors.password = 'Password is required';
      } else if (password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      } else if (!/(?=.*[a-z])/.test(password)) {
        newErrors.password = 'Password must contain at least one lowercase letter';
      } else if (!/(?=.*[A-Z])/.test(password)) {
        newErrors.password = 'Password must contain at least one uppercase letter';
      } else if (!/(?=.*\d)/.test(password)) {
        newErrors.password = 'Password must contain at least one number';
      }

      if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    // Roles validation
    if (selectedRoleIds.length === 0) {
      newErrors.roles = 'At least one role must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      if (isEditMode) {
        // Update user - only send changed fields
        const updateData: UserUpdateRequest = {
          full_name: fullName,
          email: email,
          role_ids: selectedRoleIds,
          is_active: isActive,
        };
        await onSubmit(updateData);
      } else {
        // Create user
        const createData: UserCreateRequest = {
          username,
          email,
          full_name: fullName,
          password,
          role_ids: selectedRoleIds,
          must_change_password: mustChangePassword,
        };
        await onSubmit(createData);
      }
      onClose();
    } catch (err) {
      // Error is handled by parent component
      console.error('Form submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit User' : 'Create New User'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Username field (disabled in edit mode) */}
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={!!errors.username}
            helperText={errors.username || (isEditMode ? 'Username cannot be changed' : 'Letters, numbers, and underscores only')}
            disabled={isEditMode}
            fullWidth
            required={!isEditMode}
          />

          {/* Email field */}
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            required
          />

          {/* Full name field */}
          <TextField
            label="Full Name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            error={!!errors.fullName}
            helperText={errors.fullName}
            fullWidth
            required
          />

          {/* Password fields (only in create mode) */}
          {!isEditMode && (
            <>
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!errors.password}
                helperText={errors.password || 'Min 8 characters, must include uppercase, lowercase, and number'}
                fullWidth
                required
              />

              <TextField
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                fullWidth
                required
              />
            </>
          )}

          {/* Roles multi-select */}
          <FormControl fullWidth error={!!errors.roles} required>
            <InputLabel>Roles</InputLabel>
            <Select
              multiple
              value={selectedRoleIds}
              onChange={(e) => setSelectedRoleIds(e.target.value as number[])}
              input={<OutlinedInput label="Roles" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((roleId) => {
                    const role = roles.find((r) => r.id === roleId);
                    return role ? (
                      <Chip key={roleId} label={role.display_name} size="small" />
                    ) : null;
                  })}
                </Box>
              )}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.display_name} - {role.description}
                </MenuItem>
              ))}
            </Select>
            {errors.roles && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {errors.roles}
              </Alert>
            )}
          </FormControl>

          {/* Must change password checkbox (create mode only) */}
          {!isEditMode && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={mustChangePassword}
                  onChange={(e) => setMustChangePassword(e.target.checked)}
                />
              }
              label="Require password change on first login"
            />
          )}

          {/* Active status checkbox (edit mode only) */}
          {isEditMode && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Active"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
        >
          {submitting ? 'Saving...' : isEditMode ? 'Update User' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
